import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plans } from '@/components/PaywallUI/Plans'
import { trackInitCheckout, trackPurchase } from '@/helpers/facebookPixelEvents'
import Timer from '@/components/PaywallUI/Timer'
import { PopupStatus } from '@/components/Checkout/PopupStatus'
import { PolicyAddress } from '@/components/policyAddress'
import { PaymentForm } from '@/components/Checkout/SolidGate/PaymentForm'
import { checkUserSubscription } from '@/helpers/appHelpers'
import LearnMore from '@/components/PaywallUI/LearnMore'
import { PaymentFormV2 } from '@/components/Checkout/SolidGate/PaymentFormV2'
import clsx from 'clsx'
import paywallAnimation from '@/assets/lottie/paywallAnimation.json'
import LottiePlayer from 'lottie-react'

export function CheckoutPaywall({ data }: { data: any }) {
  const paywallPlans = data.items
  const [activePlan, setActivePlan] = useState(0)
  const [userHasSubscription, setUserHasSubscription] = useState(false)
  const [paywallLoading, setPaywallLoading] = useState(true)
  const email = localStorage.getItem('userEmail')
  const isWhitePage = localStorage.getItem('isWhitePage')
  const navigate = useNavigate()
  const [successPaymentStatus, setSuccessPaymentStatus] = useState(false)

  const handlePlan = (index: number) => {
    setActivePlan(index)
  }

  useEffect(() => {
    trackInitCheckout()
    setPaywallLoading(true)
    checkUserSubscription(email ?? '').then((res) => {
      setUserHasSubscription(res.user && res.user.subscriptions.length > 0)
    })
  }, [])

  useEffect(() => {
    if (successPaymentStatus) {
      trackPurchase()
    }
  }, [successPaymentStatus])

  useEffect(() => {
    localStorage.setItem('activePlan', JSON.stringify(paywallPlans[activePlan]))
  }, [paywallPlans, activePlan])

  useEffect(() => {
    const scrollTimeout = setTimeout(() => {
      window.scrollTo(0, 0)
    }, 0)
    return () => clearTimeout(scrollTimeout)
  }, [paywallPlans])

  return (
    <>
      <div className="pt-4">
        <LottiePlayer loop={true} animationData={paywallAnimation} />
      </div>

      {paywallPlans.length > 1 && (
        <>
          <h1 className="text-[24px] font-bold tracking-[-.6px] leading-[1.3] mt-10 text-center">
            Safe Checkout
          </h1>

          <Timer version={data?.version} />

          <Plans
            version={data?.version}
            activePlan={activePlan}
            handlePlan={handlePlan}
            plans={paywallPlans}
          />

          <section
            className="w-full text-black dark:text-white mx-auto flex flex-col py-[22px] px-6 rounded-4xl mt-5 mb-8 gap-[18px]"
            style={{
              background: 'linear-gradient(270deg, #5B74FF 0%, #20319A 100%)',
            }}
          >
            <h2 className="text-[24px] leading-[32px] font-bold text-center">What you get</h2>
            <div className="grid grid-cols-2 gap-4 gap-x-1">
              {data.whatYouGetItems.map((item: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <svg className="min-w-4 h-auto" viewBox="0 0 16 16" width="16">
                    <use href="/svg/sprite.svg#check-yellow"></use>
                  </svg>
                  <p className="text-[16px] leading-[20px] font-semibold">{item}</p>
                </div>
              ))}
            </div>
          </section>

          <img
            src="/quiz/images/row-payments.png"
            alt="Money-Back Guarantee"
            className="max-w-[256px] w-full mb-8 mx-auto"
          />
          <p className="text-center text-[16px] font-semibold">Money-Back Guarantee</p>

          <PaymentForm
            plan={paywallPlans[activePlan]}
            version={data.version}
            email={email ?? ''}
            handleLoading={(val) => {
              if (paywallLoading) {
                setPaywallLoading(val)
                window.scrollTo(0, 0)
              }
            }}
            onSuccess={setSuccessPaymentStatus}
          />
        </>
      )}

      {paywallPlans.length === 1 && (
        <>
          <h1 className="text-[24px] font-bold tracking-[-.6px] leading-[1.3] mt-10 text-center">
            Optimize Your Storage
          </h1>

          <div className="relative">
            <section className="w-full text-white dark:text-white  rounded-3xl my-3  border border-[#3497607A] bg-[white]">
              <div className="p-6 flex flex-col relative">
                <p className="text-center text-[#349760] font-bold uppercase tracking-[1px] text-[12px] leading-[14px]">
                  Special offer
                </p>
                <h2 className="text-[18px] mt-1 leading-[26px] font-semibold text-center">
                  Just <span className="text-[#585F72] line-through">$39.99</span> $13.99/month
                </h2>
                <div className="grid mt-3.5 gap-0.5">
                  {data?.offerItems.map((item: string, index: number) => (
                    <div key={index} className="flex items-start gap-2">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 13 13"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="mt-[3px] max-w-4 min-w-4 h-auto"
                      >
                        <path
                          d="M5.5642 0.557463C5.93102 -0.185783 6.99086 -0.185781 7.35767 0.557465L8.57941 3.03297C8.72507 3.32811 9.00664 3.53268 9.33235 3.58001L12.0642 3.97698C12.8844 4.09616 13.212 5.10413 12.6184 5.68267L10.6416 7.60958C10.4059 7.83932 10.2984 8.17032 10.354 8.49471L10.8207 11.2156C10.9608 12.0325 10.1034 12.6554 9.36975 12.2697L6.92628 10.9851C6.63495 10.832 6.28692 10.832 5.9956 10.9851L3.55213 12.2697C2.8185 12.6554 1.96107 12.0325 2.10118 11.2156L2.56784 8.49471C2.62348 8.17032 2.51593 7.83932 2.28024 7.60958L0.303434 5.68267C-0.290083 5.10413 0.037428 4.09616 0.857648 3.97698L3.58953 3.58001C3.91524 3.53268 4.19681 3.32811 4.34247 3.03297L5.5642 0.557463Z"
                          fill="#349760"
                        />
                      </svg>
                      <p className="font-medium text-[14px] leading-[22px] dark:text-white text-[#101A36]">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <PaymentFormV2
            plan={paywallPlans[activePlan]}
            email={email ?? ''}
            version={data.version}
            paymentImage="/quiz/images/row-payments-form.png"
            handleLoading={(val) => {
              if (paywallLoading) {
                setPaywallLoading(val)
                window.scrollTo(0, 0)
              }
            }}
            onSuccess={setSuccessPaymentStatus}
          />
        </>
      )}

      <section className="mt-5 mb-8 rounded-3xl bg-[#101A360A] pt-5 pb-6 px-10">
        <h3 className="text-center mb-3 text-[16px] leading-[24px] font-semibold">
          14-day money-back guarantee
        </h3>
        <p className="text-[12px] max-w-[343px] mt-3.5 mx-auto leading-4 text-center font-medium">
          We are confident in the quality of our app - tens of thousands of users around the world
          have already optimized their photo storage with the Clean Master app. We are prepared to
          refund your money if you encountered technical issues that hindered the proper use of the
          application.
        </p>
      </section>

      <LearnMore text={paywallPlans[activePlan].text} />

      <p className="text-[12px] text-center font-medium text-[#101A36B2] hover:[&_a]:underline [&_a]:text-[#349760]">
        The order process is managed by the reseller, JOMAPP LLC. Upon completion, your contact
        information will be shared with the product vendor for support. For more details, please see
        our <a href="/privacy-policy">Privacy Policy</a> and{' '}
        <a href="/terms-of-use">Terms of Use</a>, or contact us at{' '}
        <a href="mailto:support@clean-master.app"> Contact support.</a>
      </p>

      {isWhitePage === 'true' && (
        <div className="mt-[40px]">
          <PolicyAddress />
        </div>
      )}

      {successPaymentStatus && (
        <PopupStatus
          icon="/img/success-icon.png"
          title="Your payment was successfull!"
          message="Thank you!"
          buttonTitle="Proceed To My Plan"
          onClick={() => {
            navigate('/quiz/alternative/thank-you', {
              state: { res: true },
            })
          }}
        />
      )}
      {userHasSubscription && (
        <PopupStatus
          icon="/img/success-icon.png"
          title="You already have subscription"
          buttonTitle="Proceed To Download"
          onClick={() => navigate('/quiz/alternative/thank-you')}
        />
      )}
      {paywallLoading && (
        <div
          className={clsx(
            'fixed top-0 left-0 w-full h-full  z-50 pointer-events-auto opacity-100',
            {
              'bg-[white]': data?.version === 'alternative',
              ' bg-white dark:bg-black': data?.version !== 'alternative',
            },
            data?.classes,
          )}
        >
          <div
            className={clsx(
              'absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2',
              {
                'brightness-0 invert-0': data?.version === 'alternative',
              },
              data?.classes,
            )}
          >
            <svg viewBox="0 0 100 100" height="76" className="animate-spin">
              <use href="/svg/sprite.svg#spinner"></use>
            </svg>
          </div>
        </div>
      )}
    </>
  )
}
